/**
 * useTrialGate
 *
 * Centralized gate for trial limits + premium features.
 * Wraps the paywall modal so any view can call `gate.requireRun()`
 * or `gate.requireFeature("reseller")` and get the modal automatically.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { TrialPaywallModal, type TrialPaywallReason } from "@/components/billing/TrialPaywallModal";
import { useExeibitEntitlements } from "@/hooks/useEntitlements";

interface TrialGateContextValue {
  /** Returns true if user can proceed with a new run, otherwise opens paywall. */
  requireRun: () => boolean;
  /** Returns true if feature is allowed, otherwise opens paywall. */
  requireFeature: (feature: "reseller" | "workspace" | "premium") => boolean;
  /** Manually open paywall with a reason. */
  openPaywall: (reason?: TrialPaywallReason) => void;
  /** True if user is currently on the free trial plan. */
  isTrialing: boolean;
  runsUsed: number;
  runsLimit: number;
}

const TrialGateContext = createContext<TrialGateContextValue | null>(null);

const FEATURE_TO_REASON: Record<string, TrialPaywallReason> = {
  reseller: "feature.reseller",
  workspace: "feature.workspace",
  premium: "feature.premium",
};

export function TrialGateProvider({ children }: { children: ReactNode }) {
  const { planId, canStart, limitReason, usage, entitlements } = useExeibitEntitlements();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<TrialPaywallReason>("manual");

  const isTrialing = planId === "free";

  const openPaywall = useCallback((r: TrialPaywallReason = "manual") => {
    setReason(r);
    setOpen(true);
  }, []);

  const requireRun = useCallback(() => {
    if (canStart) return true;
    setReason(limitReason ?? "limit.runs");
    setOpen(true);
    return false;
  }, [canStart, limitReason]);

  const requireFeature = useCallback(
    (feature: "reseller" | "workspace" | "premium") => {
      // Only free trial blocks these; paid plans pass through
      if (!isTrialing) return true;
      setReason(FEATURE_TO_REASON[feature] ?? "manual");
      setOpen(true);
      return false;
    },
    [isTrialing],
  );

  return (
    <TrialGateContext.Provider
      value={{
        requireRun,
        requireFeature,
        openPaywall,
        isTrialing,
        runsUsed: usage.runsStarted,
        runsLimit: entitlements.runsPerMonth,
      }}
    >
      {children}
      <TrialPaywallModal
        open={open}
        onOpenChange={setOpen}
        reason={reason}
        runsUsed={usage.runsStarted}
        runsLimit={entitlements.runsPerMonth}
      />
    </TrialGateContext.Provider>
  );
}

export function useTrialGate(): TrialGateContextValue {
  const ctx = useContext(TrialGateContext);
  if (!ctx) {
    // Graceful fallback — works even if provider is missing
    return {
      requireRun: () => true,
      requireFeature: () => true,
      openPaywall: () => {},
      isTrialing: false,
      runsUsed: 0,
      runsLimit: 0,
    };
  }
  return ctx;
}
